import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { dedupeIds, readStoredIdList } from "@/lib/storage";

const STORAGE_KEY = "worksnavi:compare";
export const COMPARE_LIMIT = 3;

interface CompareContextValue {
  compareIds: string[];
  isCompared: (id: string) => boolean;
  toggleCompare: (id: string) => void;
  removeCompare: (id: string) => void;
  /** 複数IDをまとめて外す（表示できない作品の一括解除など） */
  removeMany: (ids: string[]) => void;
  clearCompare: () => void;
  /** バックアップのインポート用（上限3件を超える分は切り捨て） */
  applyImported: (ids: string[], strategy: "merge" | "replace") => void;
  /** インポート時にストレージへ書き込んだ内容を状態へ反映する */
  hydrate: (ids: string[]) => void;
  isFull: boolean;
}

const CompareContext = createContext<CompareContextValue | null>(null);

function readStored(): string[] {
  // 壊れた値・重複IDは読み込み時に取り除く（保存キーは変更しない）
  return dedupeIds(readStoredIdList(STORAGE_KEY)).slice(0, COMPARE_LIMIT);
}

export function CompareProvider({ children }: { children: ReactNode }) {
  const [compareIds, setCompareIds] = useState<string[]>(readStored);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(compareIds));
    } catch {
      /* 保存できない環境ではセッション内のみ反映 */
    }
  }, [compareIds]);

  const toggleCompare = useCallback((id: string) => {
    const value = id.trim();
    if (!value) return;
    const key = value.toLowerCase();
    setCompareIds((current) => {
      // 取得元によって大文字・小文字が揺れても同じ作品として扱う
      if (current.some((item) => item.toLowerCase() === key)) {
        return current.filter((item) => item.toLowerCase() !== key);
      }
      if (current.length >= COMPARE_LIMIT) return [...current.slice(1), value];
      return [...current, value];
    });
  }, []);

  const removeCompare = useCallback((id: string) => {
    setCompareIds((current) => current.filter((item) => item !== id));
  }, []);

  const removeMany = useCallback((ids: string[]) => {
    const targets = new Set(ids.map((id) => id.toLowerCase()));
    setCompareIds((current) => current.filter((item) => !targets.has(item.toLowerCase())));
  }, []);

  const clearCompare = useCallback(() => setCompareIds([]), []);

  const applyImported = useCallback((ids: string[], strategy: "merge" | "replace") => {
    const incoming = dedupeIds(ids);
    setCompareIds((current) => {
      if (strategy === "replace") return incoming.slice(0, COMPARE_LIMIT);
      return dedupeIds([...current, ...incoming]).slice(0, COMPARE_LIMIT);
    });
  }, []);

  const hydrate = useCallback((ids: string[]) => {
    setCompareIds(dedupeIds(ids).slice(0, COMPARE_LIMIT));
  }, []);

  const value = useMemo<CompareContextValue>(
    () => ({
      compareIds,
      isCompared: (id: string) => compareIds.some((item) => item.toLowerCase() === id.trim().toLowerCase()),
      toggleCompare,
      removeCompare,
      removeMany,
      clearCompare,
      applyImported,
      hydrate,
      isFull: compareIds.length >= COMPARE_LIMIT,
    }),
    [compareIds, toggleCompare, removeCompare, removeMany, clearCompare, applyImported, hydrate],
  );

  return <CompareContext.Provider value={value}>{children}</CompareContext.Provider>;
}

export function useCompare(): CompareContextValue {
  const context = useContext(CompareContext);
  if (!context) throw new Error("useCompare must be used within CompareProvider");
  return context;
}